import React, { useMemo } from 'react';

import { useViewType, ViewType } from '@eika/hooks';
import { WarningLabel } from '@eika/label';
import { ModalDialogType } from '@eika/modal';

import { useConfig } from '../../../../defaultConfig';
import { avtalegiroSelector, useAccountFreeWithdrawals, useAccounts } from '../../../../fetchHooks/useAccount';
import { usePaymentAccountContext } from '../../../../hooks/useAccountState';
import useTranslation from '../../../../hooks/useTranslation';
import { useSigningContext } from '../../../../signingContext/signingContext';
import filterForInvaildAccounts from '../../../../utils/filterInvalidAccounts';
import { AccountFreeWithdrawalLabel } from '../../../shared/AccountFreeWithdrawalLabel';
import useFormContext from '../form/useFormContext';
import AvtalegiroDesktopFields from './AvtalegiroDesktopFields';
import AvtalegiroMobileFields from './AvtalegiroMobileFields';
import { FieldProps } from './FieldProps';

interface Props {
    lockAll?: boolean;
    lockKid?: boolean;
    lockReceiver?: boolean;
    animateModalAs?: ModalDialogType;
}

const AvtaleGiroFields: React.FC<Props> = ({
    lockAll = false,
    lockReceiver = false,
    lockKid = false,
    animateModalAs,
}) => {
    const translated = useTranslation();
    const { featureToggles } = useConfig();

    const { errors } = useFormContext();
    const { signingOperationPending } = useSigningContext();

    const { data: validAccounts, isLoading, refetch, isFetched } = useAccounts({ select: avtalegiroSelector });
    const { data: allAccounts } = useAccounts();
    const invalidAccounts = filterForInvaildAccounts({ allAccounts, validAccounts });

    const { targetFromAccount } = usePaymentAccountContext();
    const { data: freeWithdrawals, showFreewithdrawal } = useAccountFreeWithdrawals(
        targetFromAccount,
        featureToggles.enableGettingAccountWithdrawalsInPayment,
    );

    const viewType = useViewType();
    const isMobile = viewType === ViewType.mobile;

    const emptyFieldWarningLabel =
        isFetched && (validAccounts ?? []).length === 0 && !errors.accountId ? (
            <WarningLabel text={translated.eFaktura.noValidFromAccounts} />
        ) : undefined;

    const bottomLabel = useMemo(() => {
        if (showFreewithdrawal) {
            return <AccountFreeWithdrawalLabel freeWithdrawals={freeWithdrawals} />;
        }
        return undefined;
    }, [showFreewithdrawal, freeWithdrawals]);

    const fieldProps: FieldProps = {
        locked: lockAll || signingOperationPending,
        accounts: validAccounts ?? [],
        invalidAccounts: invalidAccounts ?? [],
        accountState: { isLoading, refetch },
        freeWithdrawals: { show: showFreewithdrawal, data: freeWithdrawals },
        animateModalAs,
        receiverLock: lockReceiver,
        kidLock: lockKid,
        bottomLabel,
        emptyFieldWarningLabel,
    };

    return isMobile ? <AvtalegiroMobileFields {...fieldProps} /> : <AvtalegiroDesktopFields {...fieldProps} />;
};

export default AvtaleGiroFields;
